/**
 * Aircraft Schedules API — Movement Ledger Model
 *
 * Each schedule is a directional record (outbound/inbound).
 * Pairs are linked by pair_id. No soft deletes — use status='cancelled'
 * for business cancellations, hard DELETE for data entry mistakes.
 *
 * Schedules are aircraft-level facts with direct aircraft_id + location_id.
 * Allocation relationship derived via temporal join (not FK).
 */

import { supabase } from '@/lib/supabase'
import type {
  AircraftSchedule,
  AircraftScheduleFormData,
  ScheduleAircraftInfo,
  ScheduleOwnerInfo,
  ScheduleDirection,
  ScheduleSource,
  ScheduleConfidence,
  ScheduleStatus,
} from '@/types/aircraft-schedules'

export const SELECT_FIELDS = `
  id,
  aircraft_id,
  location_id,
  organization_id,
  direction,
  scheduled_at,
  actual_at,
  event_id,
  pair_id,
  airport_icao,
  airport_name,
  source,
  confidence,
  status,
  flight_reference,
  notes,
  passenger_count,
  crew_count,
  diverted_to_icao,
  services_requested,
  created_at
`

function mapOwnerNested(raw: unknown): ScheduleOwnerInfo | null {
  if (!raw || typeof raw !== 'object') return null
  const c = raw as Record<string, unknown>
  const companyName = (c.company_name as string) ?? null
  const firstName = (c.first_name as string) ?? null
  const lastName = (c.last_name as string) ?? null
  const personName = [firstName, lastName].filter(Boolean).join(' ').trim() || null
  const displayName = companyName ?? personName

  // contacts is a one-to-many join (array); pick the primary, else the first.
  const contactsRaw = c.contacts as Array<Record<string, unknown>> | null | undefined
  let primaryContact: ScheduleOwnerInfo['primaryContact'] = null
  if (Array.isArray(contactsRaw) && contactsRaw.length > 0) {
    const chosen = contactsRaw.find((ct) => ct.is_primary_contact === true) ?? contactsRaw[0]
    primaryContact = {
      id: chosen.id as string,
      firstName: (chosen.first_name as string) ?? null,
      lastName: (chosen.last_name as string) ?? null,
      jobTitle: (chosen.job_title as string) ?? null,
      emailPrimary: (chosen.email_primary as string) ?? null,
      phoneMobile: (chosen.phone_mobile as string) ?? null,
    }
  }

  return {
    id: c.id as string,
    clientType: (c.client_type as string) ?? null,
    companyName,
    firstName,
    lastName,
    emailPrimary: (c.email_primary as string) ?? null,
    phonePrimary: (c.phone_primary as string) ?? null,
    displayName,
    primaryContact,
  }
}

function mapAircraftNested(raw: unknown): ScheduleAircraftInfo | null {
  if (!raw || typeof raw !== 'object') return null
  const a = raw as Record<string, unknown>
  // aircraft_type is nested inside the aircraft object from the join
  const typeObj = a.aircraft_type as Record<string, unknown> | null | undefined
  const typeCode = typeObj?.type_code as string | null
  return {
    id: a.id as string,
    tailNumber: a.tail_number as string,
    manufacturer: (a.manufacturer as string) ?? null,
    model: (a.model as string) ?? null,
    typeCode: typeCode ?? null,
    ownerClientId: (a.owner_client_id as string) ?? null,
    owner: mapOwnerNested(a.owner),
  }
}

function extractProviderCode(row: Record<string, unknown>): string | null {
  // aircraft_schedule_integrations is a one-to-many join, comes as array
  const integrations = row.aircraft_schedule_integrations as
    | Array<{ provider?: string }>
    | null
    | undefined
  if (Array.isArray(integrations) && integrations.length > 0) {
    return (integrations[0].provider as string) ?? null
  }
  return null
}

export function mapRow(row: Record<string, unknown>): AircraftSchedule {
  const aircraft = mapAircraftNested(row.aircraft)

  return {
    id: row.id as string,
    aircraftId: row.aircraft_id as string,
    locationId: row.location_id as string,
    organizationId: row.organization_id as string,
    direction: row.direction as ScheduleDirection,
    scheduledAt: row.scheduled_at as string,
    actualAt: (row.actual_at as string) ?? null,
    eventId: (row.event_id as string) ?? null,
    pairId: (row.pair_id as string) ?? null,
    airportIcao: (row.airport_icao as string) ?? null,
    airportName: (row.airport_name as string) ?? null,
    source: row.source as ScheduleSource,
    confidence: row.confidence as ScheduleConfidence,
    status: row.status as ScheduleStatus,
    flightReference: (row.flight_reference as string) ?? null,
    notes: (row.notes as string) ?? null,
    passengerCount: (row.passenger_count as number) ?? null,
    crewCount: (row.crew_count as number) ?? null,
    divertedToIcao: (row.diverted_to_icao as string) ?? null,
    servicesRequested: row.services_requested ?? null,
    createdAt: row.created_at as string,
    aircraft,
    providerCode: extractProviderCode(row),
  }
}

/**
 * Fetch all schedules for a specific aircraft
 */
export async function fetchAircraftSchedules(
  aircraftId: string
): Promise<AircraftSchedule[]> {
  const { data, error } = await supabase
    .from('aircraft_schedules')
    .select(SELECT_FIELDS)
    .eq('aircraft_id', aircraftId)
    .order('scheduled_at', { ascending: true })

  if (error) throw error
  return (data ?? []).map(mapRow)
}

/**
 * Fetch upcoming schedules for an aircraft (both directions)
 * (scheduled_at >= now, status pending, limit 5)
 */
export async function fetchUpcomingAircraftSchedules(
  aircraftId: string
): Promise<AircraftSchedule[]> {
  const now = new Date().toISOString()
  const { data, error } = await supabase
    .from('aircraft_schedules')
    .select(SELECT_FIELDS)
    .eq('aircraft_id', aircraftId)
    .gte('scheduled_at', now)
    .in('status', ['scheduled', 'confirmed'])
    .order('scheduled_at', { ascending: true })
    .limit(5)

  if (error) throw error
  return (data ?? []).map(mapRow)
}

/**
 * Create a new aircraft schedule
 */
export async function createAircraftSchedule(
  organizationId: string,
  data: AircraftScheduleFormData
): Promise<AircraftSchedule> {
  const { data: created, error } = await supabase
    .from('aircraft_schedules')
    .insert({
      organization_id: organizationId,
      aircraft_id: data.aircraft_id,
      location_id: data.location_id,
      direction: data.direction,
      scheduled_at: data.scheduled_at,
      source: data.source,
      confidence: data.confidence ?? 'confirmed',
      status: data.status ?? 'scheduled',
      actual_at: data.actual_at ?? null,
      event_id: data.event_id ?? null,
      pair_id: data.pair_id ?? null,
      airport_icao: data.airport_icao ?? null,
      airport_name: data.airport_name ?? null,
      flight_reference: data.flight_reference ?? null,
      notes: data.notes ?? null,
      passenger_count: data.passenger_count ?? null,
      crew_count: data.crew_count ?? null,
      diverted_to_icao: data.diverted_to_icao ?? null,
      services_requested: data.services_requested ?? null,
    })
    .select(SELECT_FIELDS)
    .single()

  if (error) throw error
  return mapRow(created)
}

/**
 * Update an existing aircraft schedule
 */
export async function updateAircraftSchedule(
  id: string,
  data: Partial<AircraftScheduleFormData> & {
    event_id?: string
    actual_at?: string
    pair_id?: string
  }
): Promise<AircraftSchedule> {
  const updateData: Record<string, unknown> = {}

  if (data.direction !== undefined) updateData.direction = data.direction
  if (data.scheduled_at !== undefined) updateData.scheduled_at = data.scheduled_at
  if (data.source !== undefined) updateData.source = data.source
  if (data.confidence !== undefined) updateData.confidence = data.confidence
  if (data.status !== undefined) updateData.status = data.status
  if (data.actual_at !== undefined) updateData.actual_at = data.actual_at
  if (data.event_id !== undefined) updateData.event_id = data.event_id
  if (data.pair_id !== undefined) updateData.pair_id = data.pair_id
  if (data.airport_icao !== undefined) updateData.airport_icao = data.airport_icao
  if (data.airport_name !== undefined) updateData.airport_name = data.airport_name
  if (data.flight_reference !== undefined) updateData.flight_reference = data.flight_reference
  if (data.notes !== undefined) updateData.notes = data.notes
  if (data.passenger_count !== undefined) updateData.passenger_count = data.passenger_count
  if (data.crew_count !== undefined) updateData.crew_count = data.crew_count
  if (data.diverted_to_icao !== undefined) updateData.diverted_to_icao = data.diverted_to_icao
  if (data.services_requested !== undefined) updateData.services_requested = data.services_requested

  updateData.updated_at = new Date().toISOString()

  const { data: updated, error } = await supabase
    .from('aircraft_schedules')
    .update(updateData)
    .eq('id', id)
    .select(SELECT_FIELDS)
    .single()

  if (error) throw error
  return mapRow(updated)
}

/**
 * Hard-delete an aircraft schedule (for data entry mistakes only).
 * For business cancellations, use updateAircraftSchedule(id, { status: 'cancelled' }).
 */
export async function hardDeleteAircraftSchedule(id: string): Promise<void> {
  const { error } = await supabase
    .from('aircraft_schedules')
    .delete()
    .eq('id', id)

  if (error) throw error
}

/** Extended select for location-level queries: includes aircraft + type + owner client + primary contact + integration info */
const SELECT_FIELDS_WITH_AIRCRAFT = `
  ${SELECT_FIELDS},
  aircraft:aircraft_id(
    id, tail_number, manufacturer, model, owner_client_id,
    aircraft_type:aircraft_type_id(type_code),
    owner:owner_client_id(
      id, client_type, company_name, first_name, last_name, email_primary, phone_primary,
      contacts:contacts!client_id(id, first_name, last_name, job_title, email_primary, phone_mobile, is_primary_contact)
    )
  ),
  aircraft_schedule_integrations!schedule_id(provider)
`

/**
 * Fetch all schedules for a location on a given date range.
 * Direct query — no allocation join needed.
 * Includes nested aircraft info for schedule-only items (no allocation).
 */
export async function fetchLocationSchedulesForDate(
  locationId: string,
  dateStart: string,
  dateEnd: string,
): Promise<AircraftSchedule[]> {
  // Paginate so peak days (e.g. KAGS Masters week, >1000 schedules/day) aren't
  // truncated by Supabase's default 1000-row ceiling.
  const PAGE_SIZE = 1000
  const rows: AircraftSchedule[] = []

  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await supabase
      .from('aircraft_schedules')
      .select(SELECT_FIELDS_WITH_AIRCRAFT)
      .eq('location_id', locationId)
      .gte('scheduled_at', dateStart)
      .lt('scheduled_at', dateEnd)
      .in('status', ['scheduled', 'confirmed', 'completed'])
      .order('scheduled_at', { ascending: true })
      .order('id', { ascending: true })
      .range(from, from + PAGE_SIZE - 1)

    if (error) throw error
    const page = data ?? []
    for (const row of page) rows.push(mapRow(row))
    if (page.length < PAGE_SIZE) break
  }

  return rows
}

/**
 * Fetch flight line data for an aircraft at a location.
 * Returns upcoming outbound departures AND any pending inbound return.
 */
export async function fetchFlightLineSchedules(
  aircraftId: string,
  locationId: string
): Promise<{ upcomingDepartures: AircraftSchedule[]; pendingReturn: AircraftSchedule | null }> {
  const now = new Date().toISOString()

  const [departureResult, returnResult] = await Promise.all([
    // Upcoming outbound departures (not yet departed)
    supabase
      .from('aircraft_schedules')
      .select(SELECT_FIELDS)
      .eq('aircraft_id', aircraftId)
      .eq('location_id', locationId)
      .eq('direction', 'outbound')
      .in('status', ['scheduled', 'confirmed'])
      .gte('scheduled_at', now)
      .order('scheduled_at', { ascending: true })
      .limit(3),
    // Pending inbound return (aircraft currently away, has expected return)
    supabase
      .from('aircraft_schedules')
      .select(SELECT_FIELDS)
      .eq('aircraft_id', aircraftId)
      .eq('location_id', locationId)
      .eq('direction', 'inbound')
      .in('status', ['scheduled', 'confirmed'])
      .order('scheduled_at', { ascending: true })
      .limit(1),
  ])

  if (departureResult.error) throw departureResult.error
  if (returnResult.error) throw returnResult.error

  return {
    upcomingDepartures: (departureResult.data ?? []).map(mapRow),
    pendingReturn: returnResult.data?.[0] ? mapRow(returnResult.data[0]) : null,
  }
}
